import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from 'react';

export type FilterSelectOption = {
  value: string;
  label: string;
};

export type MenuLayout = {
  top: number;
  left: number;
  width: number;
  height: number;
};

/** Altura aproximada de uma linha (padding + line-height do form-select-sm). */
export const OPTION_ROW_PX = 33;
/** Até ~8 linhas visíveis antes de rolar — alinhado ao select nativo. */
export const MENU_MAX_HEIGHT_PX = Math.round(OPTION_ROW_PX * 8.5);

export function formatMultiSelectLabel(
  selected: string[],
  options: FilterSelectOption[],
  allLabel: string,
): string {
  if (selected.length === 0) return allLabel;
  if (selected.length === 1) {
    return options.find((option) => option.value === selected[0])?.label ?? selected[0];
  }
  const labels = selected
    .map((value) => options.find((option) => option.value === value)?.label ?? value)
    .slice(0, 2);
  if (selected.length === 2) return labels.join(', ');
  return `${labels.join(', ')} (+${selected.length - 2})`;
}

export function formatSingleSelectLabel(
  value: string,
  options: FilterSelectOption[],
  placeholderLabel: string,
): string {
  if (!value) return placeholderLabel;
  return options.find((option) => option.value === value)?.label ?? value;
}

export function computeMenuLayout(trigger: HTMLElement, rowCount: number): MenuLayout {
  const rect = trigger.getBoundingClientRect();
  const contentHeight = rowCount * OPTION_ROW_PX;
  const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - 8);
  const height = Math.min(contentHeight, MENU_MAX_HEIGHT_PX, spaceBelow || MENU_MAX_HEIGHT_PX);

  return {
    top: rect.bottom,
    left: rect.left,
    width: rect.width,
    height: Math.max(height, Math.min(contentHeight, OPTION_ROW_PX * 2)),
  };
}

type FloatingMenuRefs = {
  wrapRef: RefObject<HTMLDivElement>;
  triggerRef: RefObject<HTMLElement>;
  menuRef: RefObject<HTMLUListElement>;
};

export function useFilterPanelFloatingMenu(
  open: boolean,
  rowCount: number,
  refs: FloatingMenuRefs,
  onClose: () => void,
): MenuLayout | null {
  const [menuLayout, setMenuLayout] = useState<MenuLayout | null>(null);

  const updateMenuLayout = useCallback(() => {
    if (!refs.triggerRef.current) return;
    setMenuLayout(computeMenuLayout(refs.triggerRef.current, rowCount));
  }, [refs.triggerRef, rowCount]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuLayout(null);
      return;
    }
    updateMenuLayout();
    window.addEventListener('resize', updateMenuLayout);
    window.addEventListener('scroll', updateMenuLayout, true);
    return () => {
      window.removeEventListener('resize', updateMenuLayout);
      window.removeEventListener('scroll', updateMenuLayout, true);
    };
  }, [open, updateMenuLayout]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (refs.wrapRef.current?.contains(target)) return;
      if (refs.menuRef.current?.contains(target)) return;
      onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, refs.menuRef, refs.wrapRef]);

  return menuLayout;
}
